<template>
  <modal @close_modal="close_modal" :title="$t('annotate.image_details')" :loading="loading"
  >
    <template v-slot:content>
      <p v-if="canMutateAssetMeta" class="caption mb-4">{{ $t('annotate.meta_edit_hint') }}</p>
      <p v-else-if="hasAssetId" class="caption mb-4">{{ $t('annotate.meta_readonly_role_hint') }}</p>
      <v-alert v-if="errorMessage" type="error" dense dismissible @input="errorMessage = ''">{{ errorMessage }}</v-alert>

      <div class="meta-grid">
        <div v-for="row in rows" :key="row.key" class="meta-item">
          <v-row align="center">
            <v-col cols="12" sm="3">
              <strong>{{ row.key }}</strong>
              <span v-if="row.readonly" class="text-caption d-block text-grey">{{ $t('annotate.meta_readonly') }}</span>
            </v-col>
            <v-col cols="12" :sm="canMutateAssetMeta && !row.readonly ? 7 : 9">
              <v-textarea
                  v-if="!row.readonly && canMutateAssetMeta"
                  v-model="row.value"
                  auto-grow
                  outlined
                  dense
                  rows="2"
                  hide-details
              ></v-textarea>
              <span v-else>{{ displayValue(row) }}</span>
            </v-col>
            <v-col v-if="canMutateAssetMeta && !row.readonly" cols="12" sm="2" class="text-sm-right">
              <v-btn icon small color="primary" :title="$t('annotate.meta_save')" :loading="row.saving" @click="saveRow(row)">
                <v-icon small>fas fa-save</v-icon>
              </v-btn>
              <v-btn icon small color="error" :title="$t('annotate.meta_delete')" :loading="row.deleting" @click="deleteRow(row)">
                <v-icon small>far fa-trash-alt</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>

      <div v-if="canMutateAssetMeta" class="meta-add mt-6">
        <div class="subtitle-2 mb-2">{{ $t('annotate.meta_add_entry') }}</div>
        <v-row align="start">
          <v-col cols="12" sm="4">
            <v-text-field
                v-model="newKey"
                outlined
                dense
                hide-details
                :label="$t('annotate.meta_key')"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-textarea
                v-model="newValue"
                auto-grow
                outlined
                dense
                hide-details
                rows="2"
                :label="$t('annotate.meta_value')"
            ></v-textarea>
          </v-col>
          <v-col cols="12" sm="2" class="pt-1">
            <v-btn color="primary" small :loading="adding" @click="addEntry">{{ $t('annotate.meta_add') }}</v-btn>
          </v-col>
        </v-row>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import CurrentUserMixin from '@/mixins/CurrentUserMixin.vue';

export default {
  name: "MetaInModal",
  mixins: [CurrentUserMixin],
  data () {
    return {
      loading: false,
      rows: [],
      newKey: '',
      newValue: '',
      adding: false,
      errorMessage: '',
      readOnlyKeys: ['original_category'],
    }
  },
  methods: {
    close_modal() {
      this.$emit('close_modal')
    },
    displayValue(row) {
      const v = this.meta[row.key];
      if (v !== null && typeof v === 'object') {
        return JSON.stringify(v);
      }
      return v;
    },
    serializeFromMeta(m, key) {
      const v = m[key];
      if (v === null || v === undefined) {
        return '';
      }
      if (typeof v === 'object') {
        return JSON.stringify(v);
      }
      return String(v);
    },
    parseMetaString(s) {
      if (s === '' || s === null || s === undefined) {
        return {};
      }
      try {
        const o = JSON.parse(s);
        if (o !== null && typeof o === 'object' && !Array.isArray(o)) {
          return o;
        }
        return { meta: o };
      } catch (e) {
        return {};
      }
    },
    buildRows(m) {
      if (!m || typeof m !== 'object') {
        return [];
      }
      return Object.keys(m).map((key) => ({
        key,
        value: this.serializeFromMeta(m, key),
        readonly: this.readOnlyKeys.includes(key),
        saving: false,
        deleting: false,
      }));
    },
    async saveRow(row) {
      if (!this.canMutateAssetMeta) {
        return;
      }
      this.errorMessage = '';
      row.saving = true;
      try {
        const result = await this.$api.asset.update_meta_key(this.assetId, row.key, row.value);
        if (!result || result.meta === undefined || result.meta === null) {
          this.errorMessage = this.$t('annotate.meta_save_failed');
          return;
        }
        this.$emit('meta-updated', result.meta);
        this.rows = this.buildRows(this.parseMetaString(result.meta));
      } finally {
        row.saving = false;
      }
    },
    async deleteRow(row) {
      if (!this.canMutateAssetMeta) {
        return;
      }
      this.errorMessage = '';
      row.deleting = true;
      try {
        const result = await this.$api.asset.delete_meta_key(this.assetId, row.key);
        if (!result || result.meta === undefined || result.meta === null) {
          this.errorMessage = this.$t('annotate.meta_delete_failed');
          return;
        }
        this.$emit('meta-updated', result.meta);
        this.rows = this.buildRows(this.parseMetaString(result.meta));
      } finally {
        row.deleting = false;
      }
    },
    async addEntry() {
      const key = (this.newKey || '').trim();
      if (!key) {
        this.errorMessage = this.$t('annotate.meta_key_required');
        return;
      }
      if (this.readOnlyKeys.includes(key)) {
        this.errorMessage = this.$t('annotate.meta_reserved_key');
        return;
      }
      if (!this.canMutateAssetMeta) {
        return;
      }
      this.errorMessage = '';
      this.adding = true;
      try {
        const result = await this.$api.asset.update_meta_key(this.assetId, key, this.newValue || '');
        if (!result || result.meta === undefined || result.meta === null) {
          this.errorMessage = this.$t('annotate.meta_save_failed');
          return;
        }
        this.$emit('meta-updated', result.meta);
        this.rows = this.buildRows(this.parseMetaString(result.meta));
        this.newKey = '';
        this.newValue = '';
      } finally {
        this.adding = false;
      }
    },
  },
  components: {Modal},
  props: {
    meta: {required: true},
    assetId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    hasAssetId() {
      return this.assetId !== null && this.assetId !== undefined && this.assetId !== '' && this.assetId !== false;
    },
    /** Admin / Manager on current project, or global admin — must match API asset meta rules. */
    canMutateAssetMeta() {
      return this.hasAssetId && (this.is_admin || this.is_manager || this.is_global_admin);
    },
  },
  watch: {
    meta: {
      handler(m) {
        this.rows = this.buildRows(m);
      },
      immediate: true,
      deep: true,
    },
  },
}
</script>

<style scoped>
.meta-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 10px;
}

.meta-item {
  background: rgb(247, 245, 243);
  padding: 10px;
  border: 1px solid rgb(225, 223, 219);
}
</style>
